import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { FaHeart, FaShoppingCart, FaTrash } from "react-icons/fa";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import SummaryApi from "../common";
import {
  ROUTES,
  SALE_BADGE_TEXT,
  WISHLIST_ADD_TO_CART,
  WISHLIST_EMPTY_SUBTITLE,
  WISHLIST_EMPTY_TITLE,
  WISHLIST_EXPLORE_CTA,
  WISHLIST_SAVED_LABEL,
  WISHLIST_TITLE,
} from "../constants";
import Context from "../context";
import addToCart from "../helpers/addToCart";
import displayNPRCurrency from "../helpers/displayCurrency";
import { request } from "../helpers/request";

const WishlistSkeleton = () => (
  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
    {[1, 2, 3, 4].map((n) => (
      <div
        key={n}
        className="bg-white rounded-card border border-surface-border overflow-hidden"
      >
        <div className="h-48 md:h-60 shimmer" />
        <div className="p-4 space-y-3">
          <div className="h-3 shimmer rounded-full w-1/3" />
          <div className="h-4 shimmer rounded-full w-full" />
          <div className="h-4 shimmer rounded-full w-2/3" />
          <div className="h-6 shimmer rounded-full w-1/3 mt-2" />
          <div className="h-10 shimmer rounded-xl w-full mt-3" />
        </div>
      </div>
    ))}
  </div>
);

const Wishlist = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { fetchUserAddToCart } = useContext(Context);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await request(SummaryApi.getWishlist);
      const responseData = await response.json();
      if (responseData.success) setData(responseData.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRemoveFromWishlist = async (productId) => {
    try {
      const response = await request(SummaryApi.removeFromWishlist, {
        body: JSON.stringify({ productId }),
      });
      const responseData = await response.json();
      if (responseData.success) {
        toast.success(responseData.message);
        fetchData();
      }
    } catch {
      toast.error("Failed to remove from wishlist");
    }
  };

  const handleAddToCart = async (e, id) => {
    await addToCart(e, id);
    await fetchUserAddToCart();
  };

  return (
    <div className="bg-surface-muted min-h-screen py-8 md:py-12 pb-24 lg:pb-12">
      <div className="container mx-auto px-4 md:px-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6 md:mb-10">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-red-50 rounded-2xl flex items-center justify-center text-red-500 flex-shrink-0">
              <FaHeart className="text-lg md:text-xl" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-primary tracking-tight">
                {WISHLIST_TITLE}
              </h1>
              {!loading && (
                <p className="text-sm text-neutral-500 font-medium mt-0.5">
                  {WISHLIST_SAVED_LABEL(data.length)}
                </p>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <WishlistSkeleton />
        ) : data.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="bg-white rounded-2xl md:rounded-card shadow-card border border-surface-border p-10 md:p-16 text-center max-w-md mx-auto mt-12 md:mt-20"
          >
            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6 mx-auto">
              <FaHeart className="text-red-200 text-3xl" />
            </div>
            <h2 className="text-xl md:text-2xl font-black text-primary mb-3">
              {WISHLIST_EMPTY_TITLE}
            </h2>
            <p className="text-sm text-neutral-500 mb-8 leading-relaxed">
              {WISHLIST_EMPTY_SUBTITLE}
            </p>
            <Link to={ROUTES.HOME} className="btn-primary inline-flex">
              {WISHLIST_EXPLORE_CTA}
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            <AnimatePresence>
              {data.map((item, index) => (
                <motion.div
                  key={item._id}
                  layout
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.04, duration: 0.4 }}
                  className="bg-white rounded-card border border-surface-border shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden flex flex-col group"
                >
                  {/* Image */}
                  <div className="relative h-44 sm:h-56 bg-surface-muted overflow-hidden">
                    <Link to={`/product/${item.productId?._id}`}>
                      <img
                        src={item.productId?.productImage[0]}
                        alt={item.productId?.productName}
                        className="w-full h-full object-contain mix-blend-multiply p-4 transition-transform duration-500 group-hover:scale-105"
                      />
                    </Link>

                    {/* Sale badge */}
                    {item.productId?.price > item.productId?.sellingPrice && (
                      <span className="badge-accent absolute top-3 left-3 shadow-sm">
                        {SALE_BADGE_TEXT}
                      </span>
                    )}

                    {/* Remove button */}
                    <button
                      onClick={() =>
                        handleRemoveFromWishlist(item.productId?._id)
                      }
                      aria-label="Remove from wishlist"
                      className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-neutral-400 hover:bg-red-500 hover:text-white transition-all shadow-sm border border-surface-border"
                    >
                      <FaTrash size={11} />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-4 md:p-5 flex flex-col flex-1">
                    <p className="tag text-neutral-400 mb-1">
                      {item.productId?.category}
                    </p>
                    <Link
                      to={`/product/${item.productId?._id}`}
                      className="flex-1"
                    >
                      <h3 className="text-sm md:text-base font-semibold text-primary line-clamp-2 hover:text-accent transition-colors leading-snug mb-3 min-h-[2.5rem]">
                        {item.productId?.productName}
                      </h3>
                    </Link>

                    <div className="mt-auto">
                      <div className="flex items-baseline gap-2 mb-3">
                        <span className="text-base md:text-lg font-black text-primary">
                          {displayNPRCurrency(item.productId?.sellingPrice)}
                        </span>
                        {item.productId?.price >
                          item.productId?.sellingPrice && (
                          <span className="text-xs text-neutral-400 line-through font-medium">
                            {displayNPRCurrency(item.productId?.price)}
                          </span>
                        )}
                      </div>

                      <button
                        onClick={(e) => handleAddToCart(e, item.productId?._id)}
                        className="w-full flex items-center justify-center gap-2 py-2.5 md:py-3 bg-primary text-white text-xs md:text-sm font-bold rounded-xl hover:bg-accent transition-all active:scale-95"
                      >
                        <FaShoppingCart size={13} />
                        {WISHLIST_ADD_TO_CART}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;
